<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="(showLoader && !showFilters) || (!showLoader && !applyErrorState && !getBatchfileTransfersList.length && !showFilters)"
    :showFiltersAndEmptyState="!showLoader && !applyErrorState && !getBatchfileTransfersList.length && showFilters"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    :showFloatingButton="showFilters && !applyErrorState"
    :hideHeaderButtons="!getBatchfileTransfersList.length || showLoader"
    @floatingButtonClicked="showFilterModal = true"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- We are using this slot, even tho the name is not that clear, it's the position we need for the header -->
        <template v-if="!getMobileAppState" #accounts-card-slot-area>
            <button-primary
            icon="payment-single"
            buttonSize="medium"
            :label="t('payments.batchfile.consult.newPaymentButton')"
            @clicked="navigateByName(routeList.PAYMENTS_BATCHFILE_NEW_PAYMENT_NAME)"/>
        </template>
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFiltersHandler(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
			<!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && getBatchfileTransfersList.length" class="content-wrapper">
                <table-generic
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getBatchfileTransferHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('payments.batchfile.consult.tableLoadingMore')"
                @loadMore="loadMoreHandler"
                @rowClicked="rowClickHandler($event)"
                @moreOptionsClicked="moreOptionsClickHandler"
                @downloadClicked="downloadClickedHandler"/>
            </div>
        </transition-group>
        <template #empty-state-slot>
            <!-- Empty-state -->
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getBatchfileTransfersList.length"
            :hasButton="!filterTags.length"
            buttonIcon="payment-single"
            :buttonLabel="t('payments.batchfile.consult.emptyStates.buttonLabel')"
            :illustration="emptyStateIlustration"
			:title="emptyStateTitle"
			:description="emptyStateDescription"
            @clicked="navigateByName(routeList.PAYMENTS_BATCHFILE_NEW_PAYMENT_NAME)"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal "
    emailAction="sendBatchfileEmailNotification"
    smsAction="sendBatchfileSMSNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="sendInfoData"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- ShareLinkModal -->
    <modal-centered-share-link
    v-if="showShareLinkModal"
    :operationId="sendInfoData.operationId"
    @hideMe="showShareLinkModal = false"/>
    <!-- Component to handle download logic-->
    <download-logic-component
    :downloadLink="downloadLink"
    @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
    <!-- Detail Modal -->
    <detail-modal
    v-if="showDetailModal"
    :selectedBatchFile="selectedBatchFile"
    @hideMe="showDetailModal = false"/>
    <!-- Filter Modal -->
	<filter-modal
	v-if="showFilterModal"
	:appliedFilters="appliedFilters"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
    :statusList="getStatusList"
	@hideMe="showFilterModal = false"
	@applyFilters="applyFiltersHandler($event)"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import FilterModal from '@/components/features/payments/batchfile/consult/FilterModal'
import DetailModal from '@/components/features/payments/batchfile/consult/DetailModal'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'

/* Vue & Assets */
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { getDateWithTime, getDate, createJSONDateStartDay, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'
import { ref, computed, onBeforeUnmount } from 'vue'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { clearObject } from '@/assets/scripts/objects'
import { iconMapper, iconColorMapper } from '@/assets/scripts/mappers/batchFiles'
import { createBatchfileDownloadReceiptUrl } from '@/network/api/batchfile.api.js'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestBatchfileTransfersList, requestStatusList, clearFeatureStates } = mapActions(moduleMapper.PAYMENTS_BATCHFILE_CONSULT)
const { getBatchfileTransfersList, getStatusList, getBatchfileTransferHasMore } = mapGetters(moduleMapper.PAYMENTS_BATCHFILE_CONSULT)

/* Data */
const showLoader = ref(true)
const applyErrorState = ref(false)
const tableLoadingMore = ref(false)
const selectedBatchFile = ref(null)
const showDetailModal = ref(false)
const showFilters = ref(false)
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showShareLinkModal = ref(false)
const sendInfoData = ref({ })
const downloadLink = ref(null)

/*************************************************
*                                                *
*                  Empty States                  *
*                                                *
*************************************************/
const emptyStateIlustration = computed(() => {
    if (!filterTags.value.length) return 'emptyState/batchfile-transfers'

    return 'emptyState/filters'
})
const emptyStateTitle = computed(() => {
    if (!filterTags.value.length) return t('payments.batchfile.consult.emptyStates.genericTitle')

    return t('payments.batchfile.consult.emptyStates.noFiltersTitle')
})

const emptyStateDescription = computed(() => {
    if (!filterTags.value.length) return t('payments.batchfile.consult.emptyStates.genericDescription')

    return t('payments.batchfile.consult.emptyStates.noFiltersDescription')
})

/*************************************************
*                                                *
*           BatchFile Transfers Data             *
*                                                *
*************************************************/
const tableHeaderData = [
    { label: t('payments.batchfile.consult.tableHeader.uploadDate'), dataAttr: 'uploadDate', rowCellType: 'title' },
    { label: t('payments.batchfile.consult.tableHeader.operationTypeHeader'), dataAttr: 'operationTypeHeader', rowCellType: 'description' },
    { label: t('payments.batchfile.consult.tableHeader.fileName'), dataAttr: 'fileName', rowCellType: 'description' },
    { label: t('payments.batchfile.consult.tableHeader.entryCount'), dataAttr: 'entryCount', rowCellType: 'description' },
    { label: t('payments.batchfile.consult.tableHeader.totalAmount'), dataAttr: 'totalAmount', rowCellType: 'amount' },
    { label: t('payments.batchfile.consult.tableHeader.status'), dataAttr: 'status', rowCellType: 'status' },
    { dataAttr: 'download', rowCellType: 'download' },
    { dataAttr: 'tableOptions', rowCellType: 'more-options' }
]

const tableStyles = [
    { width: '150px' },
    { width: '100px', 'text-align': 'left' },
    { width: '100px', 'text-align': 'left' },
    { width: '100px', 'text-align': 'right' },
    { width: '100px', 'text-align': 'right' },
    { width: '150px', 'text-align': 'left' },
    null,
    null
]

const tableData = computed(() => {
    return getBatchfileTransfersList.value.map(batchfile => {
        return [
            { value: batchfile.id, dataAttr: 'id' },
            {
                type: 'title',
                value: { content: getDateWithTime(batchfile.uploadDate), classes: 'capitalize' },
                dataAttr: 'uploadDate'
            },
            { type: 'description', value: batchfile.operationTypeHeader, dataAttr: 'operationTypeHeader' },
            { type: 'description', value: batchfile.fileName || '-', dataAttr: 'fileName' },
            { type: 'description', value: batchfile.entryCount || '-', dataAttr: 'entryCount' },
            {
                type: 'amount',
                value: { content: batchfile.totalAmount, currency: batchfile.currency, noBalanceIcon: true },
                dataAttr: 'totalAmount'
            },
            {
                type: 'status',
                value: {
                    text: batchfile.batchFileSubmissionStatusDescription || '',
                    icon: iconMapper(batchfile.status),
                    classes: iconColorMapper(batchfile.status)
                },
                dataAttr: 'status'
            },
            { type: 'download', dataAttr: 'download', hidden: !batchfile.hasReceipt },
            { type: 'more-options', value: moreOptions, dataAttr: 'tableOptions', hidden: !batchfile.hasReceipt }

        ]
    })
})

const rowClickHandler = row => {
	selectedBatchFile.value = getBatchfileTransfersList.value.find(transf => transf.id === row.rowId)

	showDetailModal.value = true
}

const moreOptionsClickHandler = ev => {
    selectedBatchFile.value = getBatchfileTransfersList.value.find(transf => transf.id === ev.rowId)

    sendInfoData.value = {
        id: ev.rowId,
        operationId: selectedBatchFile.value.approveStepCustomerOperation.id
    }
	moreOptions.find(obj => obj.id === ev.optionClicked).callbackFunction()
}

const downloadClickedHandler = ev => {
    downloadLink.value = createBatchfileDownloadReceiptUrl(ev)
}

const moreOptions = [
    {
        id: 1,
        text: t('payments.batchfile.consult.moreOptions.sendEmail'),
        icon: 'letter',
        callbackFunction: () => { showSendEmailInfoModal.value = true }
    }, {
        id: 2,
        text: t('payments.batchfile.consult.moreOptions.sendSms'),
        icon: 'chat',
        callbackFunction: () => { showSendSMSInfoModal.value = true }
    }, {
        id: 3,
        text: t('payments.batchfile.consult.moreOptions.generateLink'),
        icon: 'link',
        callbackFunction: () => { showShareLinkModal.value = true }
    }
]

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    batchfileTransfersList()
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    showFilters.value = false
    showLoader.value = true
    applyErrorState.value = false

    try {
        await requestStatusList()
        await requestBatchfileTransfersList({ ...paginationParams.value })

        if (getBatchfileTransfersList.value.length) showFilters.value = true

        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error, initialRequests)
    }
}

const batchfileTransfersList = async () => {
    cancelAllPendingRequests()
    applyErrorState.value = false
	showFeedbackToast.value = false

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

    const batchfileTransfersPayload = {
            ...paginationParams.value,
            ...appliedFilters.value,
            beginDate: createJSONDateStartDay(appliedFilters.value.beginDate),
            endDate: createJSONDateEndDay(appliedFilters.value.endDate)
	}
    try {
        await requestBatchfileTransfersList(batchfileTransfersPayload)
        showFilters.value = true
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		applyErrorState.value = true
        tableLoadingMore.value = false
		errorHandler(error, batchfileTransfersList)
    }
}

/*************************************************
*                                                *
*                   Filters                      *
*                                                *
*************************************************/
const showFilterModal = ref(false)
const filterTags = ref([])

const paginationParams = ref({
    pageNumber: 1
})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1
    }
}

const appliedFilters = ref({
    beginDate: '',
    endDate: '',
    minValue: '',
    maxValue: '',
    status: ''
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('payments.batchfile.consult.filters.beginDate'),
        placeholder: t('payments.batchfile.consult.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('payments.batchfile.consult.filters.endDate'),
        placeholder: t('payments.batchfile.consult.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyFiltersHandler = (filters) => {
	appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter] || appliedFilters.value[filter] === 0) {
            filterTags.value.push({
                id: filter,
                label: t(`payments.batchfile.consult.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

	resetPaginationParams()
	batchfileTransfersList()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
        case 'status':
            return getStatusList.value.find(status => status.name === value).description
		case 'minValue':
		case 'maxValue':
			return amountFormat(value, getBatchfileTransfersList.value.currency)
		default: return value
	}
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    batchfileTransfersList()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    batchfileTransfersList()
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

onBeforeUnmount(() => {
    clearFeatureStates()
})

initialRequests()
</script>

<style lang="scss" scoped>
</style>
